// src/pages/Users/Lexique/components/LexiqueToolbar.jsx

import React, { useMemo, useState } from "react";
import {
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaFileExport,
  FaHeart,
  FaRedo,
  FaSearch,
  FaShareAlt,
  FaSlidersH,
} from "react-icons/fa";

import {
  DEFAULT_LEXIQUE_UI,
  hasActiveLexiqueFilters,
} from "../utils/lexiqueUtils";

export default function LexiqueToolbar({
  ui,
  availableLetters,
  searchInput,
  setSearchInput,
  onChange,
  onReset,
  onShare,
  shareCopied,
  onExport,
  filteredCount,
  favoritesCount,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilters = useMemo(() => hasActiveLexiqueFilters(ui), [ui]);

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Rechercher un mot..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
          />
        </div>

        <ToolbarIconButton
          active={ui.favOnly}
          onClick={() => onChange({ favOnly: !ui.favOnly })}
          title={
            ui.favOnly
              ? "Afficher tout le lexique"
              : "Afficher uniquement mes favoris"
          }
        >
          <FaHeart />
        </ToolbarIconButton>

        <ToolbarIconButton
          onClick={onExport}
          title="Exporter la liste filtrée en CSV"
        >
          <FaFileExport />
        </ToolbarIconButton>

        <ToolbarIconButton
          active={shareCopied}
          onClick={onShare}
          title={shareCopied ? "Lien copié" : "Copier un lien partageable"}
        >
          {shareCopied ? <FaCheck /> : <FaShareAlt />}
        </ToolbarIconButton>

        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          className={`inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold shadow-sm transition active:scale-95 md:hidden ${
            filtersOpen
              ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900"
              : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
          }`}
        >
          <FaSlidersH />
          <span>Filtres</span>
          {filtersOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} terme{filteredCount > 1 ? "s" : ""}
          {ui.favOnly && (
            <span> · {favoritesCount} favori{favoritesCount > 1 ? "s" : ""}</span>
          )}
        </p>

        {activeFilters && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaRedo className="text-[11px]" />
            Reset
          </button>
        )}
      </div>

      <div className="mt-4 hidden space-y-4 md:block">
        <AlphabetBar
          letters={availableLetters}
          selected={ui.letter}
          onSelect={(letter) => onChange({ letter })}
        />

        <div className="grid grid-cols-2 gap-3">
          <SortSelect
            value={ui.sort}
            onChange={(value) => onChange({ sort: value })}
          />

          <div className="flex items-end justify-end">
            {activeFilters && (
              <button
                type="button"
                onClick={onReset}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaRedo />
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="mt-3 md:hidden">
          <div className="max-h-[calc(100dvh-180px)] overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-inner dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                  Affichage
                </p>
                <h3 className="text-sm font-bold text-slate-950 dark:text-white">
                  Filtrer le lexique
                </h3>
              </div>

              {activeFilters && (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  Réinitialiser
                </button>
              )}
            </div>

            <div className="space-y-4">
              <AlphabetBar
                letters={availableLetters}
                selected={ui.letter}
                onSelect={(letter) => onChange({ letter })}
              />

              <SortSelect
                value={ui.sort}
                onChange={(value) => onChange({ sort: value })}
              />
            </div>

            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              Voir les résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AlphabetBar({ letters, selected, onSelect }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        Lettre
      </p>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {letters.map((letter) => (
          <button
            key={letter}
            type="button"
            onClick={() => onSelect(letter)}
            className={`inline-flex min-h-10 shrink-0 items-center justify-center rounded-2xl px-3 text-xs font-bold transition active:scale-95 ${
              selected === letter
                ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            }`}
          >
            {letter}
          </button>
        ))}
      </div>
    </div>
  );
}

function SortSelect({ value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
        Trier par
      </span>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      >
        <option value="jp">Japonais A-Z</option>
        <option value="fr">Français A-Z</option>
      </select>
    </label>
  );
}

function ToolbarIconButton({ children, active, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl border text-base transition active:scale-95 sm:flex ${
        active
          ? "border-red-200 bg-red-50 text-red-500 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300"
          : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}